import React, { useState } from "react"

import { action, makeAutoObservable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { joinClasses, changeView, useClasses } from "../globals";
import { signIn, signOut, anonLogin } from "../simgate";

import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"

// Icons
import AutorenewIcon from "@material-ui/icons/Autorenew"
import VisibilityIcon from "@material-ui/icons/Visibility"
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff"
import { Facebook, GTranslate, Twitter } from "@material-ui/icons"

class AnonTeamStore {

  processing = false;
  selectedTeam = 0;
  renameError = false;
  tooManyError = false;
  showRenameDialog = false;
  newTeamName = "";
  newTeamNameError = false;
  selectTeamError = false;
  showCreateDialog = false;

  serverError = "";

  constructor(app) {
    makeAutoObservable(this);
    this.app = app;
  }

  async anonLogin(options) {

    const { anonSession, config, anonSettings, context } = this.app;

    runInAction(() => {
      this.processing = true;
    });

    const {
      anonEnabled,
      managementType
    } = anonSettings;

    try {
      const result = await anonLogin({
        anonEnabled,
        managementType,
        sessionId: anonSession.id,
        projectId: window.projectId,
        sessionToken: config.sessionToken,
        userId: context?.userId,
        authToken: window.localStorage.getItem("SIMGATE_TOKEN"),
        ...options
      });

      if (result?.context?.roleId && result?.context?.userId) {
        runInAction(() => {
          this.app.context = result.context;
          this.app.team = result.team;
          window.simgateContext = {
            team: result.team,
            session: result.team.session,
            context: result.context
          };
        });
      }
      else if (result?.error) {
        runInAction(() => {
          this.serverError = result.error;
        });
      }
    }
    catch (e) {
      console.error(e);
      runInAction(() => {
        this.serverError = "An unknown error occured. Please refresh and try again.";
      });
    }

    runInAction(() => {
      this.processing = false;
    });
  }

  validateTeamName() {
    const { newTeamName } = this;

    if (!Boolean(newTeamName) || newTeamName.length < 1) {
      runInAction(() => this.newTeamNameError = "Team name is required");
      return false;
    }

    if (newTeamName.length > 32) {
      runInAction(() => this.newTeamNameError = "Team name must be less than 32 characters long");
      return false;
    }

    return true;
  }

  createTeam() {
    const { newTeamName } = this;

    if (!this.validateTeamName()) { return; }

    this.anonLogin({
      selectedTeam: 0,
      newTeamName
    });
  }

  changeName() {
    const { selectedTeam, newTeamName } = this;

    if (!this.validateTeamName()) { return; }

    this.anonLogin({
      selectedTeam,
      newTeamName
    });
  }


  login() {
    const { selectedTeam } = this;

    this.anonLogin({
      selectedTeam,
      updateTeamName: "",
    });
  }


}



export const AnonTeam = observer(({ app }) => {
  const classes = useClasses();
  const [store] = useState(() => new AnonTeamStore(app));

  const {
    processing,
    selectedTeam,
    renameError,
    tooManyError,
    showRenameDialog,
    newTeamName,
    newTeamNameError,
    selectTeamError,
    showCreateDialog,
    serverError
  } = store;

  const { anonSession, anonSettings } = app;

  if (!Boolean(anonSession?.teams)) {

    return <>
      <Box
        className={joinClasses(
          classes._Padding,
          classes._Flex,
          classes._FlexDirectionColumn
        )}
      >
        <Box className={classes._Margin}>
          <Collapse in>
            <Alert severity="error">
              <Typography>
                The simulation you are looking for could not be found or the provided token is not valid.
                Double check the URL or contact your facilitator for assistance.
              </Typography>
            </Alert>
          </Collapse>
        </Box>
      </Box>
    </>
  }

  return (
    <React.Fragment>
      <Box className={classes._Margin}>
        <Paper>
          <Box
            className={joinClasses(
              classes._Padding,
              classes._TextAlignCenter,
              classes._Flex,
              classes._FlexDirectionColumn
            )}
          >
            <Box className={classes._MarginVertical}>
              <Typography variant="h3">{anonSession?.name}</Typography>
            </Box>
            <Box className={classes._MarginVertical}>
              <Typography>{anonSession?.projectName}</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      {anonSettings?.managementType === "team-based" && <>
        <Box
          className={joinClasses(
            classes._Flex,
            classes._FlexDirectionColumn,
            classes._Padding
          )}
        >
          <Box className={classes._Margin}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Team</InputLabel>
              <Select
                label="Session"
                variant="outlined"
                value={selectedTeam}
                disabled={anonSession.teams.length === 0}
                onChange={action(e => store.selectedTeam = e.target.value)}
              >
                <MenuItem value={0} disabled>{anonSession.teams.length > 0 ? "Select a team" : "No teams available. Create a new one"}</MenuItem>
                {anonSession.teams?.map(team => <MenuItem value={team.id}>{team.name}</MenuItem>)}
              </Select>
              {selectTeamError && <FormHelperText error>Select a team to join first</FormHelperText>}
              {renameError && <FormHelperText error>Select a team to rename first</FormHelperText>}
              {tooManyError && <FormHelperText error>This session has reached the maximum number of teams</FormHelperText>}
              {serverError && <FormHelperText error>{serverError}</FormHelperText>}
            </FormControl>
          </Box>
        </Box>
      </>}
      <Box
        className={joinClasses(
          classes._Flex,
          classes._FlexDirectionColumn,
          classes._Padding
        )}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          startIcon={
            processing ? (
              <AutorenewIcon className={classes.processingIcon} />
            ) : null
          }
          disabled={Boolean(processing)}
          onClick={() => { store.login() }}
        >
          {anonSettings.anonEnabled ? "Sign in" : "Register"}
        </Button>
      </Box>

      <Divider />
      <Box
        className={joinClasses(
          classes._PaddingDouble,
          classes._Flex
        )}
      >
        <Grid
          container
          spacing={2}
          className={classes._AlignItemsCenter}
        >
          {anonSettings.managementType === "team-based" && anonSettings.createTeams && <Grid item xs={anonSettings.renameTeams ? 6 : 12}>
            <Button
              fullWidth
              onClick={action(() => {
                if (anonSession.properties?.anonymousMaxTeams > 0 && anonSession.teams.length >= anonSession.properties?.anonymousMaxTeams) {
                  store.tooManyError = true;
                }
                else {
                  store.newTeamName = "";
                  store.tooManyError = false;
                  store.renameError = false;
                  store.serverError = false;
                  store.showCreateDialog = true;
                }
              })}
              disabled={processing}
            >
              New Team
            </Button>
          </Grid>}
          {anonSettings?.managementType === "team-based" && anonSettings.renameTeams && <Grid item xs={anonSettings.createTeams ? 6 : 12}>
            <Button
              fullWidth
              onClick={action(() => {
                if (!selectedTeam) {
                  store.renameError = true;
                }
                else {
                  store.newTeamName = anonSession.teams.find(team => team.id === store.selectedTeam)?.name;
                  store.renameError = false;
                  store.serverError = false;
                  store.showRenameDialog = true;
                }
              })}
              disabled={processing}
            >
              Rename Team
            </Button>
          </Grid>}
        </Grid>
      </Box>


      <Dialog open={showRenameDialog} maxWidth="xs" fullWidth onClose={action(() => store.showRenameDialog = false)}>
        <DialogTitle disableTypography>
          <Typography variant="h4">Rename Team</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={processing}
            label="Team Name"
            type="text"
            value={newTeamName}
            onFocus={action(() => {
              store.newTeamNameError = false;
            })}
            onChange={action(e => store.newTeamName = e.target.value)}
            onKeyPress={(e) => { if (e.charCode === 13) { store.changeName() } }}
            error={Boolean(newTeamNameError) || Boolean(serverError)}
            helperText={newTeamNameError || serverError}
          />
        </DialogContent>
        <DialogActions>

          <Button onClick={action(() => store.showRenameDialog = false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={e => store.changeName()}
            startIcon={
              processing ? (
                <AutorenewIcon className={classes.processingIcon} />
              ) : null
            }
            disabled={Boolean(processing)}
          >
            Change Name and {anonSettings.anonEnabled ? "Sign in" : "Register"}
          </Button>
        </DialogActions>
      </Dialog>



      <Dialog open={showCreateDialog} maxWidth="xs" fullWidth onClose={action(() => store.showCreateDialog = false)}>
        <DialogTitle disableTypography>
          <Typography variant="h4">New Team</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={processing}
            label="Team Name"
            type="text"
            value={newTeamName}
            onFocus={action(() => {
              store.newTeamNameError = false;
            })}
            onChange={action(e => store.newTeamName = e.target.value)}
            onKeyPress={(e) => { if (e.charCode === 13) { store.createTeam() } }}
            error={Boolean(newTeamNameError) || Boolean(serverError)}
            helperText={newTeamNameError || serverError}
          />
        </DialogContent>
        <DialogActions>

          <Button onClick={action(() => store.showCreateDialog = false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={e => store.createTeam()}
            startIcon={
              processing ? (
                <AutorenewIcon className={classes.processingIcon} />
              ) : null
            }
            disabled={Boolean(processing)}
          >
            Create and {anonSettings.anonEnabled ? "Sign in" : "Register"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
})

AnonTeam.title = "Sign In";